import './PromptFlows.scss';

import { FC, useContext, useEffect, useState } from 'react';
import { AppContext } from 'components/AppContext/AppContext';
import ItemPager from 'components/ItemPager/ItemPager';
import { useDataSet } from 'customHooks/useDataSet';
import { SearchType, SortDirection } from 'interfaces/DataSetOperators';
import DataWrapper from 'interfaces/DataWrapper';
import PromptFlowDataSetOperators from 'interfaces/PromptFlowDataSetOperators';
import { Entities } from 'models/Entities';
import { PromptFlow, TargetAction } from 'models/PromptFlow';
import { Form } from 'react-bootstrap';

import PromptFlowAction from '../PromptFlowAction/PromptFlowAction';

interface PromptFlowsProps {
  onExecuteAction: (action: any) => void;
}

const PromptFlows: FC<PromptFlowsProps> = ({ onExecuteAction }) => {
  const { configRepo, selectedProjectId } = useContext(AppContext);
  const pageSize = 10;
  const initialParams: PromptFlowDataSetOperators = {
    SortBy: 'TargetAction',
    SortDirection: SortDirection.Asc,
    Top: pageSize,
    Skip: 0,
    TargetActions: `${TargetAction.GenerateTopicIdeas}|${TargetAction.GenerateArticleContent}|${TargetAction.GenerateArticleTitle}|${TargetAction.GenerateArticleOutline}|${TargetAction.GenerateLinkedInPost}|${TargetAction.GenerateFacebookPost}|${TargetAction.GenerateTwitterPost}`
  };
  const { dataSet, setOperators, operators } = useDataSet<PromptFlow>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.PromptFlows,
    selectedProjectId,
    initialParams,
  );
  const dataWrapper: DataWrapper<PromptFlow> =
    dataSet.responseData as DataWrapper<PromptFlow>;
  const promptFlows: PromptFlow[] = dataWrapper?.data;

  const [searchTerms, setSearchTerms] = useState('');
  const changePage = (pageNumber: number, skip: number) => {
    const ops = { ...operators };
    ops.Skip = skip;
    ops.Top = pageSize;
    setOperators(ops);
  };


  useEffect(() => {
    if (operators.ProjectId !== selectedProjectId) {
      const ops = { ...operators };
      ops.ProjectId = selectedProjectId;
      setOperators(ops);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    const ops = { ...operators } as PromptFlowDataSetOperators;
    ops.SearchProperty = 'Name|Description|MetaKeywords';
    ops.SearchType = SearchType.Contains;
    ops.SearchValue = searchTerms.toLowerCase();
    ops.Skip = 0;
    ops.TargetActions = `${TargetAction.GenerateTopicIdeas}|${TargetAction.GenerateArticleContent}|${TargetAction.GenerateArticleTitle}|${TargetAction.GenerateArticleOutline}|${TargetAction.GenerateLinkedInPost}|${TargetAction.GenerateFacebookPost}|${TargetAction.GenerateTwitterPost}`;
    setOperators(ops);
  }, [searchTerms]);

  return (
    <div className="promptflows">
      <div className="promptflows-header p-3">
        <h3
          className="fw-semibold d-flex align-items-center mb-2">
          Prompt Chains &nbsp;<i className='bi bi-robot' />
        </h3>
        <p className="small text-muted mb-3">
          An executable set of AI steps that write your article for you — from keywords to final post.&nbsp;
          <a
            href="https://blabberjax.atlassian.net/wiki/spaces/BKP/pages/425328743/Generate+an+Article+with+AI"
            target="_blank" rel="noreferrer">
            Learn more
          </a>
        </p>
      </div>
      <Form className="input-group">
        <input
          type="text"
          placeholder='Search...'
          className="form-control"
          onChange={(evt) => setSearchTerms(evt.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          value={searchTerms} />
      </Form>
      <ItemPager
        viewAllLink='https://www.blabberjax.com/best-chat-gpt-prompts/'
        numberItemsPerPage={pageSize}
        totalNumberEntries={dataWrapper?.metaData?.totalRecordCount}
        onChangePageNumber={changePage} />
      <div className="promptflows-container mt-1">
        {promptFlows?.map((promptFlow) =>
          <PromptFlowAction onExecuteAction={onExecuteAction} promptFlow={promptFlow} key={`pf-id-${promptFlow.id}`} />
        )}
      </div>
    </div>
  );
}

export default PromptFlows;